import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  input,
  output,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';
import { APP } from 'src/app/config/app';
import { FilterOptionByIdLoader, FilterOptionLoader, IBase, IFilterOption } from 'src/app/models';
import { UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';
import { FilterOptionList } from '../filter-panel/filterOptionList';
import { TooltipPosition } from '../tooltip/tooltip-position';
import { TooltipDirective } from '../tooltip/tooltip.directive';

@Component({
  selector: 'app-column-filter',
  templateUrl: './column-filter.component.html',
  styleUrl: './column-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ColumnFilterComponent {
  readonly id = input.required<string>();
  readonly icon = input.required<string>();
  readonly loader = input.required<FilterOptionLoader>();
  readonly loadById = input<FilterOptionByIdLoader>();
  readonly toOption = input.required<(item: any) => IFilterOption>();
  readonly selected = input<IBase[]>([]);
  readonly disabled = input(false);
  readonly disabledTooltip = input('');
  readonly label = input('');

  readonly apply = output<IBase[]>();

  private readonly tooltips = viewChildren(TooltipDirective);

  readonly images = images;
  readonly TooltipPosition = TooltipPosition;
  readonly opened = signal(false);
  readonly tooltipText = computed(() => {
    if (this.disabled()) return this.disabledTooltip();
    return this.opened() ? '' : this.label();
  });
  readonly skeletonRows = Array.from({ length: APP.COLUMN_FILTER_SKELETON_ROWS });
  readonly searchTerms = new Subject<string>();

  private currentSearch = '';

  private readonly list = new FilterOptionList(
    (page, search) => this.loader()(page, search),
    (item) => this.toOption()(item),
  );

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    this.searchTerms
      .pipe(debounceTime(APP.COLUMN_FILTER_SEARCH_DEBOUNCE_MS), takeUntilDestroyed())
      .subscribe((value) => this.search(value));
  }

  get checkedCount(): number {
    return this.list.selected.length;
  }

  get loading(): boolean {
    return this.list.loading;
  }

  get loadingMore(): boolean {
    return this.list.loadingMore;
  }

  get options(): IFilterOption[] {
    const options = this.list.options;
    return [...options.filter((option) => this.isChecked(option)), ...options.filter((option) => !this.isChecked(option))];
  }

  @HostListener('window:click', ['$event'])
  onWindowClick(event: Event) {
    if (!this.opened()) return;
    const host = this.elementRef.nativeElement;
    if (this.utilsService.closeCustomModals(event, host, host)) this.opened.set(false);
  }

  async toggle() {
    if (this.disabled()) return;
    if (this.opened()) {
      this.opened.set(false);
      return;
    }
    this.opened.set(true);
    this.hideTooltips();
    this.list.selectValues(this.selected());
    this.currentSearch = '';
    await this.list.searchFor('');
    const loadById = this.loadById();
    if (loadById) await this.list.resolveSelectedLabels(loadById);
  }

  isChecked(option: IFilterOption): boolean {
    return this.list.selected.some((selected) => selected.id === option.id);
  }

  toggleOption(option: IFilterOption) {
    const selected = this.isChecked(option)
      ? this.list.selected.filter((selected) => selected.id !== option.id)
      : [...this.list.selected, option];
    this.list.selectValues(selected.map((selected) => selected.value));
    this.hideTooltips();
  }

  private hideTooltips() {
    this.tooltips().forEach((tooltip) => tooltip.destroy());
  }

  async search(value: string) {
    if (value === this.currentSearch) return;
    this.currentSearch = value;
    await this.list.searchFor(value);
  }

  async onScroll(event: Event) {
    const list = event.target as HTMLElement;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - APP.COLUMN_FILTER_SCROLL_LOAD_THRESHOLD_PX) {
      await this.list.loadMore();
    }
  }

  clean() {
    this.list.selectValues([]);
  }

  applySelection() {
    this.apply.emit(this.list.values);
    this.opened.set(false);
  }
}
